import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Loader2, ChefHat } from "lucide-react";

const NUT_ROWS = [
  ["Energia (kcal)", "kcal"],
  ["Grassi (g)", "fat"],
  ["di cui saturi (g)", "saturated"],
  ["Carboidrati (g)", "carbs"],
  ["di cui zuccheri (g)", "sugar"],
  ["Fibre (g)", "fiber"],
  ["Proteine (g)", "protein"],
  ["Sale (g)", "salt"],
];

const unitCostLabel = (unitCost, unit) => {
  if (unit === "g" || unit === "ml") {
    const per1000 = unitCost * 1000;
    return `${eur(per1000)}/${unit === "g" ? "kg" : "L"}`;
  }
  return `${eur(unitCost)}/${unit}`;
};

export default function RecipeSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { brand } = useBrand();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/recipes/${id}`)
      .then((r) => setRecipe(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  if (!recipe) return <div className="p-10">Ricetta non trovata.</div>;

  return (
    <div className="min-h-screen bg-secondary/40 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="no-print flex justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate("/ricette")} className="gap-2" data-testid="sheet-back-btn">
            <ArrowLeft className="w-4 h-4" /> Torna alle ricette
          </Button>
          <Button onClick={() => window.print()} className="rounded-full gap-2" data-testid="print-sheet-btn">
            <Printer className="w-4 h-4" /> Stampa / Salva PDF
          </Button>
        </div>

        <div className="print-area bg-white text-black rounded-lg shadow-sm p-10" data-testid="recipe-sheet">
          <div className="flex items-center justify-between border-b-2 border-black pb-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Scheda Tecnica di Produzione</p>
              <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1">{recipe.name}</h1>
            </div>
            <div className="w-14 h-14 rounded-lg bg-black flex items-center justify-center overflow-hidden shrink-0">
              {brand.logo ? (
                <img src={brand.logo} alt="logo" className="w-full h-full object-contain" />
              ) : (
                <ChefHat className="w-6 h-6 text-white" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 mt-5 text-center">
            <div><p className="text-[10px] uppercase tracking-wider text-gray-500">Porzioni</p><p className="font-mono font-bold text-lg">{recipe.portions}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-gray-500">Peso finale</p><p className="font-mono font-bold text-lg">{recipe.final_weight ?? recipe.total_weight} g{recipe.yield_loss_pct > 0 && <span className="text-[10px] text-gray-500 ml-1">(-{recipe.yield_loss_pct}%)</span>}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-gray-500">Conservazione</p><p className="font-mono font-bold text-lg">{recipe.shelf_life_days} gg</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-gray-500">Food cost</p><p className="font-mono font-bold text-lg">{recipe.food_cost_pct}%</p></div>
          </div>

          {/* Distinta base */}
          <h2 className="font-display font-bold text-lg mt-8 mb-2">Distinta base</h2>
          <table className="w-full text-sm border border-black">
            <thead>
              <tr className="border-b border-black bg-gray-100">
                <th className="text-left p-2">Componente</th>
                <th className="text-right p-2">Quantità</th>
                <th className="text-right p-2">Costo unit.</th>
                <th className="text-right p-2">Costo</th>
              </tr>
            </thead>
            <tbody>
              {recipe.items_detail.map((it, i) => (
                <tr key={i} className="border-b border-black/20">
                  <td className="p-2">
                    {it.name}
                    {it.item_type === "recipe" && <span className="text-[10px] ml-1 text-gray-500">(semilav.)</span>}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {it.quantity} {it.unit}
                    {it.waste_percent > 0 && <span className="text-[10px] text-gray-500"> (+{it.waste_percent}% scarto)</span>}
                  </td>
                  <td className="p-2 text-right font-mono">{unitCostLabel(it.unit_cost, it.unit)}</td>
                  <td className="p-2 text-right font-mono">{eur(it.line_cost)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-black font-bold">
                <td className="p-2" colSpan={3}>Costo totale</td>
                <td className="p-2 text-right font-mono">{eur(recipe.total_cost)}</td>
              </tr>
            </tbody>
          </table>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-6 mt-6">
            <div>
              <h2 className="font-display font-bold text-lg mb-2">Prezzo di vendita</h2>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-black/20"><td className="py-1.5">Costo / porzione</td><td className="py-1.5 text-right font-mono">{eur(recipe.cost_per_portion)}</td></tr>
                  <tr className="border-b border-black/20"><td className="py-1.5">Ricarico</td><td className="py-1.5 text-right font-mono">{recipe.markup_percent}%</td></tr>
                  <tr className="border-b border-black/20"><td className="py-1.5">Prezzo netto</td><td className="py-1.5 text-right font-mono">{eur(recipe.selling_price_net)}</td></tr>
                  <tr className="border-b border-black/20"><td className="py-1.5">IVA</td><td className="py-1.5 text-right font-mono">{recipe.vat_percent}%</td></tr>
                  <tr className="font-bold text-base"><td className="py-2">Prezzo di listino</td><td className="py-2 text-right font-mono">{eur(recipe.selling_price_gross)}</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <h2 className="font-display font-bold text-lg mb-2">Valori nutrizionali</h2>
              <table className="w-full text-sm border border-black">
                <thead>
                  <tr className="border-b border-black bg-gray-100">
                    <th className="text-left p-1.5">Val. medi</th>
                    <th className="text-right p-1.5">100 g</th>
                    <th className="text-right p-1.5">Porz.</th>
                  </tr>
                </thead>
                <tbody>
                  {NUT_ROWS.map(([lbl, key]) => (
                    <tr key={key} className="border-b border-black/20">
                      <td className="p-1.5">{lbl}</td>
                      <td className="p-1.5 text-right font-mono">{recipe.nutrition_per_100g?.[key] ?? 0}</td>
                      <td className="p-1.5 text-right font-mono">{recipe.nutrition_per_portion?.[key] ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {recipe.allergens?.length > 0 && (
            <div className="mt-6">
              <span className="font-bold uppercase text-sm">Allergeni: </span>
              <span className="font-bold uppercase text-sm">{recipe.allergens.join(", ")}</span>
            </div>
          )}

          {recipe.description && (
            <div className="mt-6">
              <h2 className="font-display font-bold text-lg mb-2">Procedimento</h2>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{recipe.description}</p>
            </div>
          )}

          <p className="text-[10px] text-gray-400 mt-8 border-t border-black/20 pt-3 text-center uppercase tracking-wider">
            {brand.business_name}
          </p>
        </div>
      </div>
    </div>
  );
}
